import { Component, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ApplicationInsightsService } from '../../services/application-insights/application-insights.service';
import { GlobalErrorHandler } from '../../services/global-error-handler';
import { IsBusyService } from '../../services/is-busy/is-busy.service';
import { UnitOfWorkService } from '../../services/unit-of-work/unit-of-work.service';
import * as model from '../../../model/model';
import * as modelDto from '../../../model/modelDto';

const loadingKey = 'admin:questionSorting:loadData';
const savingKey = 'admin:questionSorting:save';

@Component({
    selector: 'app-question-sorting',
    templateUrl: './question-sorting.component.html',
    styleUrls: ['./question-sorting.component.less'],
    standalone: false
})
export class QuestionSortingComponent implements OnInit {
    topics: model.Topic[] = [];
    standards: model.Standard[] = [];
    tree: modelDto.QuestionSortingTopicDto[] = [];
    selectedTopicIds: number[] = [];
    selectedStandardIds: number[] = [];
    collapsedTopicIds = new Set<number>();
    collapsedRootTopicIds = new Set<number>();
    collapsedStandardIds = new Set<string>();
    isLoading = false;
    isSaving = false;

    private originalOrder: modelDto.QuestionSortingTopicOrderDto[] = [];

    constructor(
        private uow: UnitOfWorkService,
        private appInsights: ApplicationInsightsService,
        private errorHandler: GlobalErrorHandler,
        public isBusy: IsBusyService
    ) {
        appInsights.logPageView();
    }

    ngOnInit() {
        this.updateData();
    }

    get hasChanges() {
        return JSON.stringify(this.originalOrder) !== JSON.stringify(this.captureOrder());
    }

    async onFiltersChanged() {
        if (!this.isSaving) {
            await this.loadTree();
        }
    }

    toggleTopic(topicId: number) {
        this.toggleSet(this.collapsedTopicIds, topicId);
    }

    toggleRootQuestions(topicId: number) {
        this.toggleSet(this.collapsedRootTopicIds, topicId);
    }

    toggleStandard(topicId: number, standardId: number) {
        const key = this.standardKey(topicId, standardId);
        if (this.collapsedStandardIds.has(key)) {
            this.collapsedStandardIds.delete(key);
        } else {
            this.collapsedStandardIds.add(key);
        }
    }

    isTopicCollapsed(topicId: number) {
        return this.collapsedTopicIds.has(topicId);
    }

    isRootQuestionsCollapsed(topicId: number) {
        return this.collapsedRootTopicIds.has(topicId);
    }

    isStandardCollapsed(topicId: number, standardId: number) {
        return this.collapsedStandardIds.has(this.standardKey(topicId, standardId));
    }

    hasRootQuestions(topic: modelDto.QuestionSortingTopicDto) {
        return topic.rootQuestions.length > 0;
    }

    dropStandard(event: CdkDragDrop<modelDto.QuestionSortingStandardDto[]>, topic: modelDto.QuestionSortingTopicDto) {
        if (event.previousIndex !== event.currentIndex) {
            moveItemInArray(topic.standards, event.previousIndex, event.currentIndex);
        }
    }

    dropRootQuestion(
        event: CdkDragDrop<modelDto.QuestionSortingQuestionDto[]>,
        topic: modelDto.QuestionSortingTopicDto
    ) {
        if (event.previousIndex !== event.currentIndex) {
            moveItemInArray(topic.rootQuestions, event.previousIndex, event.currentIndex);
        }
    }

    dropStandardQuestion(
        event: CdkDragDrop<modelDto.QuestionSortingQuestionDto[]>,
        standard: modelDto.QuestionSortingStandardDto
    ) {
        if (event.previousIndex !== event.currentIndex) {
            moveItemInArray(standard.questions, event.previousIndex, event.currentIndex);
        }
    }

    cancelChanges() {
        this.applyOrder(this.originalOrder);
    }

    async saveChanges() {
        if (!this.hasChanges || this.isSaving) {
            return;
        }

        this.isSaving = true;
        this.isBusy.set(savingKey, true, 'Saving question sorting');
        try {
            const result = await this.uow.saveQuestionSorting({
                topicId: this.tree.length > 0 ? this.tree[0].topicId : 0,
                standardId: null,
                standardIds: [],
                questionIds: [],
                topicOrders: this.captureOrder()
            });

            if (!result[0]) {
                throw new Error('Question sorting could not be saved.');
            }

            this.originalOrder = this.captureOrder();
        } catch (reason) {
            this.errorHandler.handleError(reason);
        } finally {
            this.isSaving = false;
            this.isBusy.set(savingKey, false);
        }
    }

    async updateData() {
        this.isBusy.set(loadingKey, true, 'Loading question sorting options');
        try {
            const [topics, standards] = await Promise.all([
                this.uow.getTopics(null, 1, 500),
                this.uow.getStandards(null, 1, 500)
            ]);

            this.topics = topics
                .filter(topic => topic.isActive)
                .sort((left, right) => left.name.localeCompare(right.name));
            this.standards = standards
                .filter(standard => standard.isActive)
                .sort((left, right) => left.name.localeCompare(right.name));
            await this.loadTree();
        } catch (reason) {
            this.errorHandler.handleError(reason);
        } finally {
            this.isBusy.set(loadingKey, false);
        }
    }

    private async loadTree() {
        this.isLoading = true;
        this.isBusy.set(loadingKey, true, 'Loading question sorting');
        try {
            this.tree = await this.uow.getQuestionSortingTree(
                this.selectedTopicIds,
                this.selectedStandardIds
            );
            this.originalOrder = this.captureOrder();
            this.collapsedTopicIds.clear();
            this.collapsedRootTopicIds.clear();
            this.collapsedStandardIds.clear();
        } catch (reason) {
            this.errorHandler.handleError(reason);
        } finally {
            this.isLoading = false;
            this.isBusy.set(loadingKey, false);
        }
    }

    private captureOrder(): modelDto.QuestionSortingTopicOrderDto[] {
        return this.tree.map(topic => ({
            topicId: topic.topicId,
            standardIds: topic.standards.map(standard => standard.standardId),
            rootQuestionIds: topic.rootQuestions.map(question => question.questionId),
            standardOrders: topic.standards.map(standard => ({
                standardId: standard.standardId,
                questionIds: standard.questions.map(question => question.questionId)
            }))
        }));
    }

    private applyOrder(order: modelDto.QuestionSortingTopicOrderDto[]) {
        const orderByTopic = new Map(order.map(topic => [topic.topicId, topic]));
        this.tree.forEach(topic => {
            const topicOrder = orderByTopic.get(topic.topicId);
            if (!topicOrder) {
                return;
            }

            topic.standards = this.orderByIds(topic.standards, topicOrder.standardIds, item => item.standardId);
            topic.rootQuestions = this.orderByIds(topic.rootQuestions, topicOrder.rootQuestionIds, item => item.questionId);
            const standardOrders = new Map(topicOrder.standardOrders.map(standard => [standard.standardId, standard.questionIds]));
            topic.standards.forEach(standard => {
                standard.questions = this.orderByIds(
                    standard.questions,
                    standardOrders.get(standard.standardId) ?? [],
                    item => item.questionId
                );
            });
        });
    }

    private orderByIds<T>(items: T[], ids: number[], getId: (item: T) => number) {
        const itemMap = new Map(items.map(item => [getId(item), item]));
        return ids.map(id => itemMap.get(id)).filter(item => !!item);
    }

    private standardKey(topicId: number, standardId: number) {
        return `${topicId}:${standardId}`;
    }

    private toggleSet(set: Set<number>, id: number) {
        if (set.has(id)) {
            set.delete(id);
        } else {
            set.add(id);
        }
    }
}
